import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { Link, useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import '../styles/Login.css';
import { GRAPHQL_HTTP_URL } from '../config/api';
import { ADMIN_LOGIN_RETURNING_KEY } from '../constants/adminLoginStorage';
import { refreshTenantBrandingFromAuth } from '../utils/tenantBranding';
import {
  ADMIN_PORTAL_ACCESS_DENIED,
  canAccessAdminPortal,
} from '../utils/adminProfileRouting';

function messageForGatewayTimeout(err, fallback) {
  const status = err?.response?.status;
  if (status === 502 || status === 503 || status === 504) {
    return 'The server took too long to respond. Please try again.';
  }
  return fallback;
}

/** Mirrors backend `UserService.isStrongPassword` for early feedback. */
function isStrongPassword(password) {
  if (!password || password.length < 7 || password.length > 14) return false;
  if (!/[A-Z]/.test(password)) return false;
  if (!/[0-9]/.test(password)) return false;
  if (!/[^A-Za-z0-9]/.test(password)) return false;
  return true;
}

const emptyDigits = () => ['', '', '', '', '', ''];

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [digits, setDigits] = useState(emptyDigits);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [infoLine, setInfoLine] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendSec, setResendSec] = useState(0);
  const otpRefs = useRef([]);

  const otp = digits.join('');

  useEffect(() => {
    if (resendSec <= 0) return undefined;
    const id = window.setInterval(() => {
      setResendSec((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [resendSec]);

  useEffect(() => {
    if (step === 'verify') {
      window.requestAnimationFrame(() => otpRefs.current[0]?.focus());
    }
  }, [step]);

  const setDigit = useCallback((index, value) => {
    const d = value.replace(/\D/g, '').slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[index] = d;
      return next;
    });
    if (d && index < 5) {
      window.requestAnimationFrame(() => otpRefs.current[index + 1]?.focus());
    }
  }, []);

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (digits[index]) return;
      if (index > 0) {
        e.preventDefault();
        otpRefs.current[index - 1]?.focus();
      }
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      otpRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      e.preventDefault();
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!text) return;
    const chars = text.split('');
    setDigits((prev) => {
      const next = [...prev];
      for (let i = 0; i < 6; i += 1) next[i] = chars[i] || '';
      return next;
    });
    const focusIdx = Math.min(chars.length, 5);
    window.requestAnimationFrame(() => otpRefs.current[focusIdx]?.focus());
  };

  const requestCode = async (e) => {
    e.preventDefault();
    setError('');
    setInfoLine('');

    if (!email.trim()) {
      setError('Email is required.');
      return;
    }

    const query = `
      mutation RequestAdminPasswordReset($input: RequestAdminPasswordResetInput!) {
        requestAdminPasswordReset(input: $input)
      }
    `;

    try {
      setLoading(true);
      const response = await axios.post(
        GRAPHQL_HTTP_URL,
        {
          query,
          variables: {
            input: { email: email.trim() },
          },
        },
        { headers: { 'Content-Type': 'application/json' } },
      );

      if (response.data?.errors?.length) {
        throw new Error(response.data.errors[0].message);
      }

      const message =
        response.data?.data?.requestAdminPasswordReset ||
        'If an account exists for this email, a verification code has been sent.';
      setInfoLine(message);
      setDigits(emptyDigits());
      setResendSec(5);
      setStep('verify');
    } catch (err) {
      setError(
        messageForGatewayTimeout(
          err,
          err?.message || 'Could not send reset code. Try again later.',
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const goToPasswordStep = (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(otp)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setStep('password');
  };

  const resendCode = async () => {
    if (resendSec > 0 || loading) return;
    setError('');
    setDigits(emptyDigits());

    const query = `
      mutation RequestAdminPasswordReset($input: RequestAdminPasswordResetInput!) {
        requestAdminPasswordReset(input: $input)
      }
    `;

    try {
      setLoading(true);
      const response = await axios.post(
        GRAPHQL_HTTP_URL,
        {
          query,
          variables: {
            input: { email: email.trim() },
          },
        },
        { headers: { 'Content-Type': 'application/json' } },
      );

      if (response.data?.errors?.length) {
        throw new Error(response.data.errors[0].message);
      }

      const message =
        response.data?.data?.requestAdminPasswordReset ||
        'If an account exists for this email, a verification code has been sent.';
      setInfoLine(message);
      setResendSec(5);
      window.requestAnimationFrame(() => otpRefs.current[0]?.focus());
    } catch (err) {
      setError(
        messageForGatewayTimeout(
          err,
          err?.message || 'Could not resend code. Try again later.',
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (!/^\d{6}$/.test(otp)) {
      setError('Enter the 6-digit code from your email.');
      setStep('verify');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!isStrongPassword(newPassword)) {
      setError(
        'Password must be 7–14 characters and include at least 1 uppercase letter, 1 special character, and 1 digit.',
      );
      return;
    }

    const query = `
      mutation ResetAdminPasswordAndLogin($input: ResetAdminPasswordWithOtpInput!) {
        resetAdminPasswordAndLogin(input: $input) {
          id
          roles
          permissions
          name
          accessToken
          company {
            id
            companyName
          }
        }
      }
    `;

    try {
      setLoading(true);
      const response = await axios.post(
        GRAPHQL_HTTP_URL,
        {
          query,
          variables: {
            input: {
              email: email.trim(),
              otp,
              newPassword,
            },
          },
        },
        { headers: { 'Content-Type': 'application/json' } },
      );

      if (response.data?.errors?.length) {
        throw new Error(response.data.errors[0].message);
      }

      const data = response.data?.data?.resetAdminPasswordAndLogin;
      if (data?.accessToken) {
        if (!canAccessAdminPortal(data.roles || [])) {
          throw new Error(ADMIN_PORTAL_ACCESS_DENIED);
        }
        localStorage.setItem(ADMIN_LOGIN_RETURNING_KEY, '1');
        const cookieOpts = {
          expires: 1,
          path: '/',
          sameSite: 'strict',
          secure: window.location.protocol === 'https:',
        };
        Cookies.set('accessToken', data.accessToken, cookieOpts);
        if (data.id) Cookies.set('userId', String(data.id), cookieOpts);
        Cookies.set('companyId', data.company.id, cookieOpts);
        Cookies.set('companyName', data.company.companyName, cookieOpts);
        Cookies.set('userName', data.name, cookieOpts);
        localStorage.setItem('userRoles', JSON.stringify(data.roles || []));
        localStorage.setItem('userPermissions', JSON.stringify(data.permissions || []));
        void refreshTenantBrandingFromAuth();
        navigate('/', { replace: true });
        return;
      }

      throw new Error('Password updated but sign-in failed.');
    } catch (err) {
      setError(
        messageForGatewayTimeout(
          err,
          err?.message || 'Could not reset password. Please try again.',
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const displayEmail = email.trim();

  const heroLeft = (
    <div className="ld-left">
      <div className="ld-hero">
        <img
          src="/getstarted2.png"
          alt="SAS Cleaning Suite"
          className="ld-hero-img"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
            const logo = e.currentTarget.parentElement.querySelector('.ld-hero-logo');
            if (logo) logo.style.display = 'block';
          }}
        />
        <img
          src="/logoSas.png?v=5"
          alt="SAS Logo"
          className="ld-hero-logo"
          style={{ display: 'none' }}
        />
      </div>
      <p className="ld-tagline">Trusted by cleaning businesses</p>
      <p className="ld-tagline-sub">
        Manage your operations, schedule, and team all in one place.
      </p>
    </div>
  );

  return (
    <div className="ld-root">
      <div className="ld-grid">
        {heroLeft}

        <div className="ld-right">
          <div style={{ marginBottom: '1.5rem' }}>
            {step === 'password' ? (
              <>
                <button
                  type="button"
                  className="ld-link-btn"
                  style={{ marginTop: 0, marginBottom: '0.35rem' }}
                  onClick={() => {
                    setStep('verify');
                    setError('');
                  }}
                >
                  ← Back to code
                </button>
                <Link
                  to="/login"
                  className="ld-link-btn"
                  style={{ marginTop: 0, marginBottom: '0.75rem' }}
                >
                  ← Back to login
                </Link>
                <h2 className="ld-heading">Create New Password</h2>
                <p className="ld-subheading">
                  Set a strong password to protect your account. You&apos;ll use this to log in from
                  now on.
                </p>
              </>
            ) : (
              <>
                <Link to="/login" className="ld-link-btn" style={{ marginTop: 0, marginBottom: '0.75rem' }}>
                  ← Back to login
                </Link>
                {step === 'email' && (
                  <>
                    <h2 className="ld-heading">Forgot password?</h2>
                    <p className="ld-subheading">
                      Enter the email associated with your account and we&apos;ll send a code to reset
                      your password.
                    </p>
                  </>
                )}
                {step === 'verify' && (
                  <>
                    <h2 className="ld-heading">Verify Code</h2>
                    <p className="ld-subheading">
                      Enter the 6-digit code sent to {displayEmail || 'your email'}
                    </p>
                  </>
                )}
              </>
            )}
          </div>

          {step === 'email' && (
            <form className="ld-form" onSubmit={requestCode} noValidate>
              {error && <div className="ld-error">{error}</div>}
              <div className="ld-field">
                <label className="ld-label" htmlFor="fp-email">
                  Email address
                </label>
                <input
                  id="fp-email"
                  className="ld-input"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                  disabled={loading}
                />
              </div>
              <button type="submit" className="ld-submit" disabled={loading}>
                {loading ? 'Sending…' : 'Send Reset Code'}
              </button>
            </form>
          )}

          {step === 'verify' && (
            <form className="ld-form" onSubmit={goToPasswordStep} noValidate>
              {error && <div className="ld-error">{error}</div>}
              {infoLine && <div className="ld-info">{infoLine}</div>}
              <div className="ld-field">
                <span className="ld-label">Verification code</span>
                <div className="ld-otp-cells" onPaste={handleOtpPaste}>
                  {digits.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => {
                        otpRefs.current[i] = el;
                      }}
                      className="ld-otp-cell"
                      type="text"
                      inputMode="numeric"
                      autoComplete={i === 0 ? 'one-time-code' : 'off'}
                      maxLength={1}
                      value={digit}
                      onChange={(e) => setDigit(i, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                      disabled={loading}
                      aria-label={`Digit ${i + 1} of 6`}
                    />
                  ))}
                </div>
              </div>
              <button type="submit" className="ld-submit" disabled={loading}>
                Verify OTP
              </button>
              <button
                type="button"
                className="ld-link-btn"
                onClick={resendCode}
                disabled={loading || resendSec > 0}
              >
                {resendSec > 0 ? `Resend code in ${resendSec}s` : 'Resend code'}
              </button>
              <button
                type="button"
                className="ld-link-btn"
                onClick={() => {
                  setStep('email');
                  setError('');
                  setDigits(emptyDigits());
                  setInfoLine('');
                  setResendSec(0);
                }}
              >
                ← Use a different email
              </button>
            </form>
          )}

          {step === 'password' && (
            <form className="ld-form" onSubmit={submitNewPassword} noValidate>
              {error && <div className="ld-error">{error}</div>}
              <div className="ld-field">
                <label className="ld-label" htmlFor="fp-account-email">
                  Account email
                </label>
                <input
                  id="fp-account-email"
                  className="ld-input"
                  type="email"
                  value={displayEmail}
                  readOnly
                  tabIndex={-1}
                  aria-readonly="true"
                />
              </div>
              <div className="ld-field">
                <label className="ld-label" htmlFor="fp-new">
                  New password
                </label>
                <div className="ld-pw-wrap">
                  <input
                    id="fp-new"
                    className="ld-input"
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="7–14 characters: upper, digit, special"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    maxLength={14}
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="ld-pw-toggle"
                    onClick={() => setShowNewPassword((v) => !v)}
                    aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                  >
                    {showNewPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
              <div className="ld-field">
                <label className="ld-label" htmlFor="fp-confirm">
                  Confirm new password
                </label>
                <div className="ld-pw-wrap">
                  <input
                    id="fp-confirm"
                    className="ld-input"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Re-enter new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    maxLength={14}
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="ld-pw-toggle"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
              <button type="submit" className="ld-submit" disabled={loading}>
                {loading ? 'Saving…' : 'Update Password'}
              </button>
            </form>
          )}

          <div className="ld-footer">
            <p>
              By continuing, you agree to our{' '}
              <Link to="/terms-conditions" className="ld-footer-link">
                Terms &amp; Conditions
              </Link>{' '}
              and{' '}
              <Link to="/data-usage-policy" className="ld-footer-link">
                Privacy Policy
              </Link>
            </p>
            <p className="ld-signup-link">
              Don&apos;t have an account?{' '}
              <span onClick={() => window.open('https://sascleaningsuite.com', '_blank')}>
                Sign up
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
